import { format, isValid } from "date-fns";

import { parseNumericValue } from "./numberFormatter";

const LOCK_PERIOD_DETAILS = {
  flex: {
    title: "Flex Yield",
    period: 0,
    unit: "flex",
  },
  sixMonths: {
    title: "6 Month Yield",
    period: 6,
    unit: "month",
  },
  twelveMonths: {
    title: "12 Month Yield",
    period: 12,
    unit: "month",
  },
  threeYears: {
    title: "3 Year Yield",
    period: 3,
    unit: "year",
  },
  fiveYears: {
    title: "5 Year Yield",
    period: 5,
    unit: "year",
  },
};

const getLockPeriodDetails = (lockPeriod) =>
  Object.entries(LOCK_PERIOD_DETAILS).find(
    ([periodKey]) => periodKey === lockPeriod
  )?.[1] || {
    title: "ZAR8 Yield",
    period: 0,
    unit: "",
  };

const getPositionAmount = (position) =>
  parseNumericValue(
    position?.zar8Amount ??
      position?.lockedAmount ??
      position?.amount ??
      position?.valueInZar
  );

export const getZar8YieldPositionsFromResponse = (positionsResponse) =>
  Array.isArray(positionsResponse?.positions)
    ? positionsResponse.positions
    : [];

export const getZar8YieldHistoryFromResponse = (historyResponse) =>
  Array.isArray(historyResponse?.items) ? historyResponse.items : [];

export const getZar8YieldTotalLockedFromResponse = (positionsResponse) =>
  parseNumericValue(positionsResponse?.summary?.totalLocked);

export const getZar8YieldLifetimeEarnedFromResponse = (positionsResponse) =>
  parseNumericValue(positionsResponse?.summary?.totalBtcEarnedLifetime);

export const mapZar8YieldPositionToLedgerEntry = (position) => {
  if (!position) {
    return null;
  }

  const amount = getPositionAmount(position);
  const details = getLockPeriodDetails(position.lockPeriod);
  const positionId = position._id || position.id || "";

  return {
    _id: positionId,
    ledgerId: positionId,
    productId: {
      _id: positionId,
      assetId: {
        asset: "ZAR8",
      },
      duration: {
        period: details.period,
        unit: details.unit,
      },
      durationInYearOrMonth: [details.period, details.unit],
      name: details.title,
      status: position.status,
      value: amount,
      valueInLocalCurrency: amount,
      valueInZar: amount,
      withdrawalFee: {
        feeType: "fixed",
        feeAmount: 0,
      },
    },
    amount,
    asset: "ZAR8",
    capital: amount,
    createdAt: position.createdAt,
    duration: {
      period: details.period,
      unit: details.unit,
    },
    maturityDate: position.maturityDate,
    name: details.title,
    profit: parseNumericValue(position.totalRewardEarned),
    rewardAsset: position.rewardAsset || "BTC",
    startDate: position.startDate,
    status: position.status,
    valueInLocalCurrency: amount,
    valueInZar: amount,
    rawPosition: position,
  };
};

export const mapZar8YieldPositionsToLedgerEntries = (positions = []) =>
  positions.map(mapZar8YieldPositionToLedgerEntry).filter(Boolean);

const getHistoryTitle = (type) => {
  if (type === "locked") {
    return "Locked ZAR8";
  }

  if (type === "unlocked") {
    return "Unlocked ZAR8";
  }

  if (type === "yield") {
    return "Yield payout";
  }

  return "ZAR8 Yield activity";
};

const formatHistoryDate = (value) => {
  const dateValue = value ? new Date(value) : null;

  return dateValue && isValid(dateValue)
    ? format(dateValue, "dd MMM yyyy")
    : "Recently";
};

export const mapZar8YieldHistoryToDashboardEntries = ({
  history = [],
  formatter,
}) =>
  history.slice(0, 4).map((entry, index) => {
    const amountZar = parseNumericValue(entry?.zarAmount ?? entry?.amount);
    const type = `${entry?.type || "activity"}`.toLowerCase();
    const rewardAsset = entry?.rewardAsset || "BTC";
    const cryptoEstimate = parseNumericValue(entry?.cryptoEstimate);

    return {
      id: entry?.id || `${type}-${entry?.date || index}`,
      title: getHistoryTitle(type),
      subtitle:
        cryptoEstimate > 0 ? `${rewardAsset} ${cryptoEstimate}` : "ZAR8 Yield",
      amountLabel:
        amountZar > 0
          ? formatter.format(amountZar, "ZAR")
          : formatter.format(0, "ZAR"),
      status: entry?.status || "success",
      dateLabel: formatHistoryDate(entry?.date),
    };
  });
